import { useImperativeHandle, forwardRef } from "react";
import * as XLSX from "xlsx";
import GTM from "../../GTM";
import { useSelector } from "react-redux";
import {
    CONVERGE_TYPE,
    INCLUDE_HISTORICAL_LEAVE,
    PAYMENT_STATUS,
    PERIOD_SCHEDULE_TYPE,
    PLAN_REQUEST_STATUS,
    POLICIES_AND_DIVISIONS,
    PROTECTION_STATUS,
    REPORTING_GROUP,
    REPORTING_PERIOD,
    STATUS,
    APPLY_REPORTING_PERIOD_TO,
    LEAVE_SCHEDULE_TYPE,
} from "../../constants";
import {
    formatDate,
    getExcelExportNumberFormat,
    getExcelExportTemporalType,
    getAccessoryKey,
    resolveExcelExportTemporalValue,
    toExcelTemporalSerial,
} from "../../utils/helpers";
import PropTypes from "prop-types";
import {
    shouldUsePreviousDay,
    isDecimalExportColumn,
    resolveExcelExportNumericValue,
    EXCEL_DECIMAL_FORMAT,
} from "unmsght-report-package";

const ExportToCSV = forwardRef(
    (
        {
            data,
            defaultColumns,
            selectedReport,
            reportLastFetched,
            userEmail,
            portalUserApiError,
            reportingPeriodValue,
            reportingStatusValue,
            selectedCoverageType,
            selectedViewDataType,
            includeHistoricalLeaveValue,
            periodScheduleTypeValue,
            leaveScheduleTypeValue,
            periodStatusValue,
            protectionStatusValue,
            disclaimer,
            organizationsAccess,
            ssnDisplayMode,
            organizationName,
            relatedOrganizationNames,
            selectedPolicyDivision,
            selectedReportingGroup,
            selectedPaymentStatus,
            columnFilters,
            enableCoverageType,
            enableStatusFilter,
            enablePeriodScheduleTypeFilter,
            enableLeaveScheduleTypeFilter,
            enablePeriodStatusFilter,
            enableProtectionStatusFilter,
            enableIncludeHistoricalLeave,
            enableReportGroupFilter,
            enablePolicyandDivisisonFilter,
            enablePaymentStatus,
            formattedHiddenFilters,
            visibleColumnsInOrder,
            reportViewType,
            subviewMap,
            enableApplyReportingPeriodFilter,
            selectedApplyingReportingPeriod,
        },
        ref
    ) => {
        const gtm = new GTM(null);

        const maxLength = 32767; // Excel cell limit

        function splitAndPushData(label, dataString, dataArray) {
            if (dataString.length > maxLength) {
                const splitByCommaSpace = dataString.split(", ");
                let chunk = "";
                let chunksArray = [];

                splitByCommaSpace.forEach((item) => {
                    if (chunk.length + item.length + 2 > maxLength) {
                        chunksArray.push(chunk);
                        chunk = item;
                    } else {
                        chunk += (chunk ? ", " : "") + item;
                    }
                });
                if (chunk) chunksArray.push(chunk);

                chunksArray.forEach((chunk, index) => {
                    dataArray.push([index === 0 ? label : "", chunk]);
                });
            } else {
                dataArray.push([label, dataString]);
            }
        }

        const { user } = useSelector((state) => state.auth);

        // Helper functions to build different sections of the second page data
        const buildBasicInfoSection = () => {
            return [
                ["Report", selectedReport?.Name],
                [],
                ["Date export run", reportLastFetched],
                [],
                ["Organization Name", organizationName],
                [],
                [
                    "Related Organizations",
                    relatedOrganizationNames?.length > 0
                        ? relatedOrganizationNames
                        : "None",
                ],
                [],
                [
                    "Run by user",
                    user.isUserInternal ? "Unum Internal" : userEmail,
                ],
                [],
                ["Additional filters / Column filters", "Column filter values"],
            ];
        };

        const buildColumnFiltersSection = (data) => {
            if (columnFilters?.length > 0) {
                columnFilters.forEach((filter) =>
                    data.push([filter.columnName, filter.filterValue])
                );
            } else {
                data.push(["None", "None"]);
            }
            return data;
        };

        const buildDisclaimerSection = (data) => {
            data.push([" "]);
            data.push(["Disclaimer", disclaimer]);
            data.push([]);
            data.push(["Filter", "Filter Values"]);
            return data;
        };
        const buildReportingPeriodSection = (data) => {
            if (portalUserApiError) {
                return data;
            }

            const openReports = [
                "All Open Disability",
                "All Open Leaves",
                "All Open Absences",
            ];
            if (openReports.includes(selectedReport.SubViewName)) {
                data.push([REPORTING_PERIOD, "All Open"]);
            } else {
                const usePreviousDay = shouldUsePreviousDay(
                    selectedReport.SubViewName,
                    reportingPeriodValue.name
                );
                const fromDate = formatDate(
                    reportingPeriodValue?.value?.effectiveStartDt
                );
                const toDate = usePreviousDay
                    ? fromDate
                    : formatDate(reportingPeriodValue?.value?.effectiveEndDt);

                data.push(
                    [REPORTING_PERIOD, reportingPeriodValue.name],
                    ["From date", fromDate],
                    ["To date", toDate]
                );
            }
            return data;
        };

        const addFilterIfEnabled = (
            data,
            isEnabled,
            hiddenFilter,
            filterName,
            filterValue
        ) => {
            if (isEnabled || formattedHiddenFilters.includes(hiddenFilter)) {
                data.push([filterName, filterValue]);
            }
            return data;
        };

        const getFilterValues = (filterArray, field = "name") => {
            if (!Array.isArray(filterArray)) return "";
            return filterArray
                .map((item) => item?.[field])
                .filter(Boolean)
                .join(", ");
        };

        const buildFiltersSections = (data) => {
            if (portalUserApiError) {
                return data;
            }

            // Coverage Type Filter
            const statusKey =
                selectedViewDataType === "Payments" ? PAYMENT_STATUS : STATUS;
            let coverageTypeValue;

            if (
                ["Payments", "Claims"].includes(subviewMap) &&
                reportViewType === "Life"
            ) {
                coverageTypeValue = "Life & AD&D";
            } else {
                coverageTypeValue = getFilterValues(selectedCoverageType);
            }
            // Add all the simple filters
            data = addFilterIfEnabled(
                data,
                enableCoverageType,
                CONVERGE_TYPE,
                CONVERGE_TYPE,
                coverageTypeValue
            );

            data = addFilterIfEnabled(
                data,
                enableStatusFilter,
                statusKey,
                statusKey,
                getFilterValues(reportingStatusValue)
            );

            data = addFilterIfEnabled(
                data,
                enablePaymentStatus,
                PAYMENT_STATUS,
                PAYMENT_STATUS,
                getFilterValues(selectedPaymentStatus)
            );

            data = addFilterIfEnabled(
                data,
                enableIncludeHistoricalLeave,
                INCLUDE_HISTORICAL_LEAVE,
                INCLUDE_HISTORICAL_LEAVE,
                getFilterValues(includeHistoricalLeaveValue, "name")
            );

            data = addFilterIfEnabled(
                data,
                enablePeriodScheduleTypeFilter,
                PERIOD_SCHEDULE_TYPE,
                LEAVE_SCHEDULE_TYPE,
                getFilterValues(periodScheduleTypeValue)
            );

            data = addFilterIfEnabled(
                data,
                enableLeaveScheduleTypeFilter,
                LEAVE_SCHEDULE_TYPE,
                LEAVE_SCHEDULE_TYPE,
                getFilterValues(leaveScheduleTypeValue)
            );

            data = addFilterIfEnabled(
                data,
                enablePeriodStatusFilter,
                PLAN_REQUEST_STATUS,
                PLAN_REQUEST_STATUS,
                getFilterValues(periodStatusValue)
            );

            data = addFilterIfEnabled(
                data,
                enableProtectionStatusFilter,
                PROTECTION_STATUS,
                PROTECTION_STATUS,
                getFilterValues(protectionStatusValue)
            );

            data = addFilterIfEnabled(
                data,
                enableApplyReportingPeriodFilter,
                APPLY_REPORTING_PERIOD_TO,
                APPLY_REPORTING_PERIOD_TO,
                getFilterValues(selectedApplyingReportingPeriod)
            );

            // Handle the filters that may need splitting due to length
            if (enablePolicyandDivisisonFilter) {
                const policiesDivisions =
                    selectedPolicyDivision
                        ?.filter((item) => item?.includes("-"))
                        .join(", ") || "";
                splitAndPushData(
                    POLICIES_AND_DIVISIONS,
                    policiesDivisions,
                    data
                );
            }

            if (enableReportGroupFilter) {
                const reportingGroup = getFilterValues(selectedReportingGroup);
                splitAndPushData(REPORTING_GROUP, reportingGroup, data);
            }

            return data;
        };

        const prepareSecondPageData = () => {
            let data = buildBasicInfoSection();
            data = buildColumnFiltersSection(data);
            data = buildDisclaimerSection(data);
            data = buildReportingPeriodSection(data);
            data = buildFiltersSections(data);
            return data;
        };

        const filterData = (ssnDisplayMode) => {
            const ssnAccess = organizationsAccess?.SSNACCESS;

            const columnHeaderDetails = visibleColumnsInOrder.map(
                (key) => defaultColumns.find((col) => col.id === key) || {}
            );
            const temporalExportTypes = columnHeaderDetails.map((column) =>
                getExcelExportTemporalType(column)
            );
            const headerRow = columnHeaderDetails.map(
                (key) => key?.header || "Default Header"
            );
            const keysToInclude = columnHeaderDetails.map(
                (key) =>
                    getAccessoryKey(reportViewType, key?.accessorKey) ||
                    "defaultKey"
            );
            const currencyCellMatrix = [];
            const temporalCellMatrix = [];
            const formattedData = data.map((row) => {
                const rowData = [];
                const rowCurrencyFlags = [];
                const rowTemporalTypes = [];

                keysToInclude.forEach((key, columnIndex) => {
                    let value =
                        row[key] === undefined || row[key] === null
                            ? ""
                            : row[key];
                    if (key === "taxIdentificationNumber" || key === "ssn") {
                        if (ssnAccess === "No Access") {
                            rowData.push("No Access");
                            rowCurrencyFlags.push(false);
                            rowTemporalTypes.push(null);
                            return;
                        }
                        switch (ssnDisplayMode) {
                            case 0:
                                rowData.push("***-**-****");
                                rowCurrencyFlags.push(false);
                                rowTemporalTypes.push(null);
                                return;
                            case 1:
                                rowData.push(value?.substr(-4) || "");
                                rowCurrencyFlags.push(false);
                                rowTemporalTypes.push(null);
                                return;
                            case 2:
                                rowData.push(value || "");
                                rowCurrencyFlags.push(false);
                                rowTemporalTypes.push(null);
                                return;
                        }
                    }

                    const temporalExportType = temporalExportTypes[columnIndex];
                    if (temporalExportType) {
                        const temporalExportValue =
                            resolveExcelExportTemporalValue(
                                value,
                                temporalExportType
                            );
                        if (temporalExportValue) {
                            rowData.push(temporalExportValue.parsedValue);
                            rowCurrencyFlags.push(false);
                            rowTemporalTypes.push(
                                temporalExportValue.resolvedType
                            );
                            return;
                        }
                    }

                    if (
                        typeof value === "string" &&
                        /^\-?\$[\d,]+(\.\d{2})?$/.test(value.trim())
                    ) {
                        const numericValue = Number(
                            value.replace(/[\$,]/g, "")
                        );
                        rowData.push(numericValue);
                        rowCurrencyFlags.push(true); // Mark this cell for $ formatting
                        rowTemporalTypes.push(null);
                    } else {
                        rowData.push(value);
                        rowCurrencyFlags.push(false);
                        rowTemporalTypes.push(null);
                    }
                });
                currencyCellMatrix.push(rowCurrencyFlags);
                temporalCellMatrix.push(rowTemporalTypes);
                return rowData;
            });
            return {
                filteredData: [headerRow, ...formattedData],
                currencyCellMatrix: [
                    [...Array(headerRow.length).fill(false)],
                    ...currencyCellMatrix,
                ],
                temporalCellMatrix: [
                    [...Array(headerRow.length).fill(null)],
                    ...temporalCellMatrix,
                ],
            };
        };

        const exportToExcel = () => {
            const wb = XLSX.utils.book_new();
            const { filteredData, currencyCellMatrix, temporalCellMatrix } =
                filterData(ssnDisplayMode);

            const ws1 = XLSX.utils.aoa_to_sheet(filteredData);
            ws1["!cols"] = filteredData[0].map(() => ({ wpx: 100 }));
            const range = XLSX.utils.decode_range(ws1["!ref"]);
            for (let row = range.s.r + 1; row <= range.e.r; row++) {
                for (let col = range.s.c; col <= range.e.c; col++) {
                    const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                    const cell = ws1[cellRef];
                    const temporalExportType = temporalCellMatrix[row][col];
                    if (cell && temporalExportType) {
                        const parsedTemporalValue = filteredData[row][col];

                        if (parsedTemporalValue) {
                            cell.t = "n";
                            cell.v = toExcelTemporalSerial(parsedTemporalValue);
                            cell.z =
                                getExcelExportNumberFormat(temporalExportType);
                            continue;
                        }
                    }

                    if (cell && currencyCellMatrix[row][col]) {
                        cell.t = "n";
                        cell.z = "$#,##0.00";
                    }
                }
            }

            // Additive decimal typing: only columns the backend tags
            // `sortingMethod: "decimal"` are written as real, summable Excel
            // numbers (2-decimal format).
            const decimalColumnFlags = visibleColumnsInOrder.map((key) =>
                isDecimalExportColumn(
                    defaultColumns.find((column) => column.id === key) || {}
                )
            );
            for (let row = range.s.r + 1; row <= range.e.r; row++) {
                for (let col = range.s.c; col <= range.e.c; col++) {
                    if (!decimalColumnFlags[col]) {
                        continue;
                    }
                    const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
                    const cell = ws1[cellRef];
                    if (!cell) {
                        continue;
                    }
                    const numericExportValue = resolveExcelExportNumericValue(
                        filteredData[row][col]
                    );
                    if (!numericExportValue) {
                        continue;
                    }
                    cell.t = "n";
                    cell.v = numericExportValue.parsedValue;
                    cell.z = EXCEL_DECIMAL_FORMAT;
                }
            }
            XLSX.utils.book_append_sheet(wb, ws1, "Data");

            const secondPageData = prepareSecondPageData();
            const ws2 = XLSX.utils.aoa_to_sheet(secondPageData);
            ws2["!cols"] = secondPageData[0].map(() => ({ wpx: 100 }));
            XLSX.utils.book_append_sheet(wb, ws2, "Filter criteria");

            XLSX.writeFile(wb, `${selectedReport?.Name}.xlsx`);

            gtm.Event(
                "custom_event",
                "button",
                "click",
                "export to csv",
                "report detail",
                selectedViewDataType,
                "export_report",
                ""
            );
        };

        useImperativeHandle(ref, () => ({
            exportToExcel,
        }));

        return null;
    }
);

ExportToCSV.propTypes = {
    data: PropTypes.array,
    defaultColumns: PropTypes.array,
    selectedReport: PropTypes.object,
    reportLastFetched: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.instanceOf(Date),
    ]),
    userEmail: PropTypes.string,
    portalUserApiError: PropTypes.bool,
    reportingPeriodValue: PropTypes.object,
    reportingStatusValue: PropTypes.array,
    selectedCoverageType: PropTypes.array,
    selectedViewDataType: PropTypes.string,
    includeHistoricalLeaveValue: PropTypes.array,
    periodScheduleTypeValue: PropTypes.array,
    leaveScheduleTypeValue: PropTypes.array,
    periodStatusValue: PropTypes.array,
    protectionStatusValue: PropTypes.array,
    disclaimer: PropTypes.string,
    organizationsAccess: PropTypes.object,
    ssnDisplayMode: PropTypes.number,
    organizationName: PropTypes.string,
    relatedOrganizationNames: PropTypes.array,
    selectedPolicyDivision: PropTypes.array,
    selectedReportingGroup: PropTypes.array,
    selectedPaymentStatus: PropTypes.array,
    columnFilters: PropTypes.array,
    enableCoverageType: PropTypes.bool,
    enableStatusFilter: PropTypes.bool,
    enablePeriodScheduleTypeFilter: PropTypes.bool,
    enableLeaveScheduleTypeFilter: PropTypes.bool,
    enablePeriodStatusFilter: PropTypes.bool,
    enableProtectionStatusFilter: PropTypes.bool,
    enableIncludeHistoricalLeave: PropTypes.bool,
    enableReportGroupFilter: PropTypes.bool,
    enablePolicyandDivisisonFilter: PropTypes.bool,
    enablePaymentStatus: PropTypes.bool,
    formattedHiddenFilters: PropTypes.array,
    visibleColumnsInOrder: PropTypes.array,
    reportViewType: PropTypes.string,
    subviewMap: PropTypes.string,
    selectedApplyingReportingPeriod: PropTypes.array,
    enableApplyReportingPeriodFilter: PropTypes.bool,
};

export default ExportToCSV;
